import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import './Contacts.css';

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function Contacts({ onSelectContact, showSelectButton = true, onContactsLoaded }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    email: '',
    notes: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
        const response = await api.get('/api/contacts');
        if (response.data.success) {
          const contactsList = response.data.contacts || [];
          setContacts(contactsList);
          if (onContactsLoaded) {
            onContactsLoaded(contactsList);
          }
        }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      // Only show error if it's a real error, not just empty contacts
      if (error.response?.status === 401 || error.response?.status === 403) {
        setError('Authentication failed. Please login again.');
      } else if (error.response?.status >= 500) {
        setError('Server error. Please try again later.');
      } else {
        // For other errors, just log but don't show error message
        // Empty contacts array is not an error
        setContacts([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingContact) {
        // Update contact
        const response = await api.put(`/api/contacts/${editingContact.id}`, formData);
        if (response.data.success) {
          setSuccess('Contact updated successfully');
          setShowForm(false);
          setEditingContact(null);
          resetForm();
          fetchContacts();
        }
      } else {
        // Add new contact
        const response = await api.post('/api/contacts', formData);
        if (response.data.success) {
          setSuccess('Contact added successfully');
          setShowForm(false);
          resetForm();
          fetchContacts();
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save contact');
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name,
      phone_number: contact.phone_number,
      email: contact.email || '',
      notes: contact.notes || ''
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }

    try {
      const response = await api.delete(`/api/contacts/${id}`);
      if (response.data.success) {
        setSuccess('Contact deleted successfully');
        fetchContacts();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete contact');
    }
  };

  const handleSelectContact = (contact) => {
    if (onSelectContact) {
      onSelectContact(contact.phone_number);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone_number: '',
      email: '',
      notes: ''
    });
    setEditingContact(null);
  };

  const handleCancel = () => {
    setShowForm(false);
    resetForm();
    setError('');
    setSuccess('');
  };

  return (
    <div className="contacts-container">
      <div className="contacts-header">
        <h2>📇 Contacts</h2>
        <button 
          className="add-contact-btn"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          ➕ Add Contact
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>
          {success}
        </div>
      )}

      {showForm && (
        <div className="contact-form-container">
          <h3>{editingContact ? 'Edit Contact' : 'Add New Contact'}</h3>
          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter contact name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone_number">Phone Number *</label>
              <input
                type="text"
                id="phone_number"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleInputChange}
                placeholder="e.g., 919876543210"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter email (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Additional notes (optional)"
                rows="3"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="save-btn">
                {editingContact ? '💾 Update' : '➕ Add'}
              </button>
              <button type="button" className="cancel-btn" onClick={handleCancel}>
                ❌ Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading contacts...</div>
      ) : error && (error.includes('Authentication') || error.includes('Server error') || error.includes('Failed to load')) ? (
        <div className="no-contacts">
          <p style={{ color: '#c33', fontWeight: '600' }}>⚠️ {error}</p>
        </div>
      ) : contacts.length === 0 ? (
        <div className="no-contacts">
          <p>📭 No contacts found. Add your first contact!</p>
        </div>
      ) : (
        <div className="contacts-list">
          {contacts.map((contact) => (
            <div key={contact.id} className="contact-card">
              <div className="contact-info">
                <div className="contact-name">{contact.name}</div>
                <div className="contact-phone">📱 {contact.phone_number}</div>
                {contact.email && (
                  <div className="contact-email">📧 {contact.email}</div>
                )}
                {contact.notes && (
                  <div className="contact-notes">📝 {contact.notes}</div>
                )}
              </div>
              <div className="contact-actions">
                {onSelectContact && showSelectButton && (
                  <button
                    className="select-btn"
                    onClick={() => handleSelectContact(contact)}
                    title="Use this number"
                  >
                    ✓ Use
                  </button>
                )}
                <button
                  className="edit-btn"
                  onClick={() => handleEdit(contact)}
                  title="Edit contact"
                >
                  ✏️ Edit
                </button>
                <button
                  className="delete-btn"
                  onClick={() => handleDelete(contact.id)}
                  title="Delete contact"
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Contacts;

